import _isEmpty from 'lodash/isEmpty';

import { Link } from '@reach/router';
import PropTypes from 'prop-types';
import { useCallback, useContext, useMemo, useState } from 'react';

import { CommonContextV2, CommonLayoutV2, CustomButtonV2, CustomInputV2, CustomSnackbarV2 } from 'components/v2';

import { axios } from 'services/axios';
import { COMMON_ERROR_MESSAGE, errorMessageMap, ERRORS_CODE, NETWORK_ERROR_MESSAGE } from 'services/Constant';
import { logService } from 'services/LogService';
import QueryParametersParser from 'services/QueryParametersParser';
import Validator from 'services/Validator';

import './confirm-email.scss';

const ConfirmEmailPageV2 = ({ location }) => {
    const { query } = useContext(CommonContextV2);

    const [email, setEmail] = useState(location?.state?.email ?? '');

    const [isLoading, setIsLoading] = useState(false);
    const [isResend, setIsResend] = useState(!!location?.state?.tryResend);
    const [hasResentEmail, setHasResentEmail] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);

    const errors = useMemo(() => (!Validator.isEmail(email) && email ? ['Invalid Email Address'] : []), [email]);

    const onSubmit = useCallback(
        (e) => {
            e && e.preventDefault();
            setIsLoading(true);
            setErrorMessages([]);
            (async () => {
                try {
                    const request = await axios.post('/resend', { email, query });
                    if (request.status === 200) {
                        setHasResentEmail(true);
                    }
                } catch (err) {
                    logService.error(err);
                    let title;
                    const messages = [];
                    if (
                        (err?.response?.status >= 400 && err?.response?.status < 500) ||
                        errorMessageMap(err?.response?.data?.error?.code)
                    ) {
                        title = 'Error';
                        messages.push(COMMON_ERROR_MESSAGE);
                    } else {
                        title = 'Error';
                        messages.push(NETWORK_ERROR_MESSAGE);
                    }

                    if (err?.response?.data?.error?.code !== ERRORS_CODE.notAuthorizedException.code) {
                        messages.push(errorMessageMap(err?.response?.data?.error?.code));
                    }

                    setErrorMessages([
                        {
                            title,
                            error: err,
                            messages
                        }
                    ]);
                }
                setIsLoading(false);
            })();
        },
        [email, query]
    );

    const onClickResend = (e) => {
        e && e.preventDefault();
        setIsResend(true);
        setHasResentEmail(false);
    };

    const confirmEmailForm = useMemo(() => {
        if (isResend && !hasResentEmail) {
            return (
                <>
                    <div className='confirm-email__content'>
                        <h3>Resend Activation Email</h3>
                    </div>
                    <CustomInputV2
                        required
                        autoFocus
                        label='Please enter your registered email address'
                        name='email'
                        type='text'
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && errors?.length === 0 && email) {
                                onSubmit(e);
                            }
                        }}
                        placeholder='Enter your email'
                        errors={errors}
                        value={email}
                        disabled={isLoading}
                        highlightErrors={!_isEmpty(errorMessages)}
                    />
                    <CustomButtonV2
                        disabled={!email || errors?.length || isLoading}
                        loading={isLoading}
                        onClick={onSubmit}
                    >
                        Submit
                    </CustomButtonV2>
                    {!_isEmpty(errorMessages) && <CustomSnackbarV2 messageList={errorMessages} />}
                </>
            );
        }

        if (isResend && hasResentEmail) {
            return (
                <>
                    <div className='confirm-email__content'>
                        <h3>Activation email resent.</h3>
                    </div>
                    <div className='confirm-email__divider' />
                    <p>
                        If no email is received within ten minutes, check that the submitted address is correct or try
                        to{' '}
                        <Link to={'/resetPassword' + QueryParametersParser.toString(query)} state={{ email }}>
                            Reset your Password
                        </Link>
                        .
                    </p>
                </>
            );
        }

        return (
            <>
                <div className='confirm-email__content'>
                    <h3>Thank you for registering.</h3>
                    <p>
                        A verification email has been sent to your registered email address. To complete the account
                        creation process, please confirm your account by clicking the verification button provided in
                        the email.
                    </p>
                </div>
                <div className='confirm-email__divider' />
                <p>
                    Didn't get the activation email? Please check your Spam Folder or click{' '}
                    <Link to={'/confirmEmail' + QueryParametersParser.toString(query)} onClick={onClickResend}>
                        here
                    </Link>{' '}
                    to resend the email. If no email is received within ten minutes, check that the submitted address is
                    correct or try to{' '}
                    <Link to={'/resetPassword' + QueryParametersParser.toString(query)} state={{ email }}>
                        Reset your Password
                    </Link>
                    .
                </p>
                <Link to={'/login' + QueryParametersParser.toString(query)}>
                    <CustomButtonV2>Back to Sign In</CustomButtonV2>
                </Link>
            </>
        );
    }, [email, errors, isResend, hasResentEmail, isLoading, query, errorMessages, onSubmit]);

    const pageTitle = useMemo(
        () => (isResend ? 'OPSWAT Accounts | Resend Email' : 'OPSWAT Accounts | Thank you for registering'),
        [isResend]
    );

    return (
        <CommonLayoutV2
            isLoading={false}
            pageTitle={pageTitle}
            contentStyles={{
                gap: '40px'
            }}
        >
            <div className='confirm-email__container'>{confirmEmailForm}</div>
        </CommonLayoutV2>
    );
};

export default ConfirmEmailPageV2;

ConfirmEmailPageV2.propTypes = {
    location: PropTypes.object
};
